/**
 * DatasetSelector - Select existing dataset or create new one with full configuration
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, Plus } from 'lucide-react';
import { uploadApi } from '@/lib/api/upload';
import { schemaEndpoints } from '@/lib/api/schemas';
import type { Dataset, DatasetType, DatasetFormat, CreateDatasetRequest } from '@/features/upload/types/upload.types';

interface DatasetSelectorProps {
  value: CreateDatasetRequest | null;
  onChange: (dataset: CreateDatasetRequest | null) => void;
  error?: string;
  disabled?: boolean;
}

const CREATE_NEW_VALUE = '__create_new__';

// Dataset type options
const DATASET_TYPES: { value: DatasetType; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'view', label: 'View' },
  { value: 'materialized_view', label: 'Materialized View' },
  { value: 'external', label: 'External' },
];

// Dataset format options
const DATASET_FORMATS: { value: DatasetFormat; label: string }[] = [
  { value: 'parquet', label: 'Parquet' },
  { value: 'csv', label: 'CSV' },
  { value: 'json', label: 'JSON' },
  { value: 'avro', label: 'Avro' },
  { value: 'orc', label: 'ORC' },
  { value: 'delta', label: 'Delta Lake' },
  { value: 'iceberg', label: 'Apache Iceberg' },
];

/**
 * Generate qualified name from dataset name
 */
function generateQualifiedName(name: string): string {
  return `lakehouse.datasets.${name}`;
}

/**
 * Generate storage location from dataset name
 */
function generateLocation(name: string): string {
  return `/data/lakehouse/datasets/${name}/`;
}

export const DatasetSelector: React.FC<DatasetSelectorProps> = ({
  value: _value, // Controlled value passed from parent (not used internally, state is local)
  onChange,
  error,
  disabled = false,
}) => {
  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExistingDataset, setSelectedExistingDataset] = useState('');

  // New dataset form state
  const [newDatasetName, setNewDatasetName] = useState('');
  const [datasetType, setDatasetType] = useState<DatasetType>('table');
  const [datasetFormat, setDatasetFormat] = useState<DatasetFormat>('parquet');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [schemaId, setSchemaId] = useState<string>('');

  // Fetch existing datasets
  const { data: datasetsResponse, isLoading: datasetsLoading } = useQuery({
    queryKey: ['datasets'],
    queryFn: () => uploadApi.listDatasets(),
  });

  // Fetch schemas for selection
  const { data: schemasResponse, isLoading: schemasLoading } = useQuery({
    queryKey: ['schemas'],
    queryFn: () => schemaEndpoints.list({ size: 100 }),
  });

  const datasets = datasetsResponse?.datasets || [];
  const schemas = schemasResponse?.data?.items || [];

  // Filter datasets based on search query
  const filteredDatasets = useMemo(() => {
    if (!datasets.length) return [];
    if (!searchQuery) return datasets;

    const query = searchQuery.toLowerCase();
    return datasets.filter(
      (dataset: Dataset) =>
        dataset.name.toLowerCase().includes(query) ||
        dataset.description?.toLowerCase().includes(query)
    );
  }, [datasets, searchQuery]);

  // Build CreateDatasetRequest from form state
  const buildDatasetRequest = useCallback((): CreateDatasetRequest | null => {
    if (!newDatasetName) return null;

    return {
      name: newDatasetName,
      qualified_name: generateQualifiedName(newDatasetName),
      type: datasetType,
      location: generateLocation(newDatasetName),
      format: datasetFormat,
      description: description || undefined,
      tags: tags.length > 0 ? tags : undefined,
      schema_id: schemaId || undefined,
    };
  }, [newDatasetName, datasetType, datasetFormat, description, tags, schemaId]);

  // Update parent when form changes in create mode
  const updateParent = useCallback(() => {
    if (mode === 'create') {
      const request = buildDatasetRequest();
      onChange(request);
    }
  }, [mode, buildDatasetRequest, onChange]);

  // Effect to update parent when form changes
  React.useEffect(() => {
    updateParent();
  }, [newDatasetName, datasetType, datasetFormat, description, tags, schemaId, mode]);

  const handleSelectChange = (selectedValue: string) => {
    if (selectedValue === CREATE_NEW_VALUE) {
      setMode('create');
      setSelectedExistingDataset('');
      // Reset form to defaults
      setNewDatasetName('');
      setDatasetType('table');
      setDatasetFormat('parquet');
      setDescription('');
      setTags([]);
      setSchemaId('');
      onChange(null);
    } else {
      setMode('select');
      setSelectedExistingDataset(selectedValue);
      // For existing datasets, we need to find the dataset and build a request
      const existingDataset = datasets.find((d: Dataset) => d.name === selectedValue);
      if (existingDataset) {
        onChange({
          name: existingDataset.name,
          qualified_name: generateQualifiedName(existingDataset.name),
          type: 'table', // Default for existing
          location: generateLocation(existingDataset.name),
          format: 'parquet', // Default for existing
          description: existingDataset.description,
        });
      }
    }
  };

  const handleNewDatasetNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setNewDatasetName(name);
  };

  const handleAddTag = () => {
    if (newTag && !tags.includes(newTag)) {
      setTags([...tags, newTag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const validateDatasetName = (name: string): string | null => {
    if (!name) return 'Dataset name is required';
    if (name.length < 3) return 'Dataset name must be at least 3 characters';
    if (name.length > 63) return 'Dataset name must be less than 63 characters';
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(name) && name.length > 1) {
      return 'Dataset name must start and end with alphanumeric characters and contain only lowercase letters, numbers, and hyphens';
    }
    if (name.length === 1 && !/^[a-z0-9]$/.test(name)) {
      return 'Dataset name must contain only lowercase letters and numbers';
    }
    return null;
  };

  const newDatasetError = mode === 'create' && newDatasetName ? validateDatasetName(newDatasetName) : null;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="dataset-selector">Dataset</Label>

        {mode === 'select' ? (
          <div className="space-y-2">
            <Input
              type="text"
              placeholder="Search datasets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={disabled || datasetsLoading}
              className="mb-2"
            />

            <Select value={selectedExistingDataset} onValueChange={handleSelectChange} disabled={disabled}>
              <SelectTrigger id="dataset-selector" aria-label="Select dataset">
                <SelectValue placeholder="Select a dataset" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CREATE_NEW_VALUE}>
                  <span className="font-semibold">+ Create New Dataset</span>
                </SelectItem>

                {datasetsLoading && (
                  <SelectItem value="__loading__" disabled>
                    Loading datasets...
                  </SelectItem>
                )}

                {!datasetsLoading && filteredDatasets.length === 0 && searchQuery && (
                  <SelectItem value="__no_results__" disabled>
                    No datasets found
                  </SelectItem>
                )}

                {!datasetsLoading &&
                  filteredDatasets
                    .sort((a: Dataset, b: Dataset) => a.name.localeCompare(b.name))
                    .map((dataset: Dataset) => (
                      <SelectItem key={dataset.name} value={dataset.name}>
                        <div className="flex flex-col">
                          <span>{dataset.name}</span>
                          {dataset.description && (
                            <span className="text-xs text-muted-foreground">
                              {dataset.description}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>

            {datasets && datasets.length === 0 && !datasetsLoading && (
              <p className="text-sm text-muted-foreground">
                No datasets available. Create a new one to get started.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4 rounded-lg border p-4">
            {/* Dataset Name */}
            <div className="space-y-2">
              <Label htmlFor="new-dataset-name">Name *</Label>
              <Input
                id="new-dataset-name"
                type="text"
                placeholder="my-new-dataset"
                value={newDatasetName}
                onChange={handleNewDatasetNameChange}
                disabled={disabled}
                aria-label="New dataset name"
                className={newDatasetError ? 'border-destructive' : ''}
              />
              {newDatasetError && (
                <p className="text-sm text-destructive" role="alert">
                  {newDatasetError}
                </p>
              )}
            </div>

            {/* Type and Format row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Dataset Type */}
              <div className="space-y-2">
                <Label htmlFor="dataset-type">Type *</Label>
                <Select value={datasetType} onValueChange={(v) => setDatasetType(v as DatasetType)} disabled={disabled}>
                  <SelectTrigger id="dataset-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATASET_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dataset Format */}
              <div className="space-y-2">
                <Label htmlFor="dataset-format">Format *</Label>
                <Select value={datasetFormat} onValueChange={(v) => setDatasetFormat(v as DatasetFormat)} disabled={disabled}>
                  <SelectTrigger id="dataset-format">
                    <SelectValue placeholder="Select format" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATASET_FORMATS.map((format) => (
                      <SelectItem key={format.value} value={format.value}>
                        {format.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Schema Selection */}
            <div className="space-y-2">
              <Label htmlFor="dataset-schema">Schema (optional)</Label>
              <Select value={schemaId || '__none__'} onValueChange={(v) => setSchemaId(v === '__none__' ? '' : v)} disabled={disabled || schemasLoading}>
                <SelectTrigger id="dataset-schema">
                  <SelectValue placeholder={schemasLoading ? "Loading schemas..." : "Select a schema (optional)"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">
                    <span className="text-muted-foreground">No schema</span>
                  </SelectItem>
                  {schemas
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((schema) => (
                      <SelectItem key={schema.id} value={schema.id}>
                        <div className="flex flex-col">
                          <span>{schema.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {schema.field_count} fields
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="dataset-description">Description (optional)</Label>
              <Textarea
                id="dataset-description"
                placeholder="Describe your dataset..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={disabled}
                rows={2}
              />
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label htmlFor="dataset-tags">Tags (optional)</Label>
              <div className="flex gap-2">
                <Input
                  id="dataset-tags"
                  type="text"
                  placeholder="Add a tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={handleTagKeyPress}
                  disabled={disabled}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleAddTag}
                  disabled={disabled || !newTag}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-destructive"
                        disabled={disabled}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Auto-generated fields preview */}
            {newDatasetName && !newDatasetError && (
              <div className="rounded-md bg-muted p-3 text-sm space-y-1">
                <p><span className="font-medium">Qualified Name:</span> {generateQualifiedName(newDatasetName)}</p>
                <p><span className="font-medium">Location:</span> {generateLocation(newDatasetName)}</p>
              </div>
            )}

            {/* Back button */}
            <button
              type="button"
              onClick={() => {
                setMode('select');
                setNewDatasetName('');
                onChange(null);
              }}
              className="text-sm text-primary hover:underline"
              disabled={disabled}
            >
              ← Back to existing datasets
            </button>

            {/* Dataset naming guidelines */}
            <Alert>
              <AlertDescription className="text-xs">
                Dataset names must:
                <ul className="mt-1 ml-4 list-disc space-y-1">
                  <li>Be 3-63 characters long</li>
                  <li>Start and end with alphanumeric characters</li>
                  <li>Contain only lowercase letters, numbers, and hyphens</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>

      {error && mode === 'select' && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
